/* eslint-disable */
import React, { useState, useEffect, useCallback } from 'react';
import { ref, set } from 'firebase/database';
import { database, goalsAPI, conversationsAPI, businessPlansAPI } from '../firebase';
import { aiChat, aiChatStream } from '../lib/aiApi';
import { MENTOR_SYSTEM_PREAMBLE } from '../lib/mentorPersona';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import '../styles/ManifestationPage.css';

const ManifestationPage = ({ user, userGoal, onBack, onComplete }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [showBusinessPlan, setShowBusinessPlan] = useState(false);
  const [visiblePhases, setVisiblePhases] = useState(0);
  const [showFullPlan, setShowFullPlan] = useState(false);
  const [businessPlan, setBusinessPlan] = useState(null);
  
  // Enhanced goal data state
  const [goalData, setGoalData] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [goalCategory, setGoalCategory] = useState('general');
  const [expertQuestions, setExpertQuestions] = useState([]);
  // Plan generation state
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);
  const [planGenerationFailed, setPlanGenerationFailed] = useState(false);
  // Plan generation state - used in sidebar display
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [questionAnswers, setQuestionAnswers] = useState({});
  const [isWaitingForConfirmation, setIsWaitingForConfirmation] = useState(false);
  const [assessmentComplete, setAssessmentComplete] = useState(false);
  
  // Conversation interval tracking
  const [questionConversations, setQuestionConversations] = useState({});
  const [currentQuestionStartIndex, setCurrentQuestionStartIndex] = useState(0);
  
  // Voice conversation states - DISABLED for better text-based assessment
  const [isListening, setIsListening] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false); // Disabled
  const [speechSynthesis, setSpeechSynthesis] = useState(null);
  
  // Voice recording states
  const [isRecording, setIsRecording] = useState(false);
  const [speechRecognition, setSpeechRecognition] = useState(null);
  const [currentTranscript, setCurrentTranscript] = useState('');
  // Voice-related state - DISABLED
  const conversationMode = false; // Disabled
  // const initialMessageSpoken = true; // Skip voice messages - kept to avoid breaking voice code - unused
  const [autoVoiceMode, setAutoVoiceMode] = useState(false); // Disabled
  const [isSpeaking, setIsSpeaking] = useState(false);
  // Voice-related states - DISABLED for text-only assessment
  const [availableVoices, setAvailableVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [recentAIText, setRecentAIText] = useState(''); // Track recent AI speech to prevent feedback
  const [microphoneMuted, setMicrophoneMuted] = useState(false); // Track microphone muting
  
  // Voice 2.0 Enhanced States - DISABLED for text-only assessment
  const voiceStreamingEnabled = false; // Disabled
  const voicePersonality = 'friendly'; // Default to friendly
  const voiceSpeechRate = 1.1; // Slightly faster by default
  const voiceVolume = 0.9;
  const voiceInterruptable = false; // Disabled
  const [conversationContext, setConversationContext] = useState([]);
  const [voiceCommandMode, setVoiceCommandMode] = useState(false);
  // Voice streaming functions - no-op for text-only mode  
  const setIsStreamingResponse = () => {}; // No-op function to avoid errors
  const setStreamingText = () => {}; // No-op function to avoid errors
  // const voiceWaveform = []; // Static empty array - kept to avoid breaking voice code - unused
  // const autoVoiceActivated = false; // Kept to avoid breaking voice code - unused
  
  const recognitionRef = React.useRef(null);
  const speechTimeoutRef = React.useRef(null);
  const silenceTimeoutRef = React.useRef(null);
  const streamingTimeoutRef = React.useRef(null);

  // Enhanced feedback prevention: Text similarity calculation
  const calculateTextSimilarity = (text1, text2) => {
    if (!text1 || !text2) return 0;
    
    const words1 = text1.toLowerCase().split(/\s+/);
    const words2 = text2.toLowerCase().split(/\s+/);
    
    if (words1.length === 0 || words2.length === 0) return 0;
    
    // Check for exact substring matches (high feedback risk)
    if (text1.includes(text2) || text2.includes(text1)) return 1.0;
    
    // Count common words
    const commonWords = words1.filter(word => 
      word.length > 2 && words2.includes(word)
    );
    
    const similarity = (commonWords.length * 2) / (words1.length + words2.length);
    return Math.min(similarity, 1.0);
  };

  // Initialize voice features - DISABLED for text-only assessment
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Voice features disabled - skip speech recognition initialization
    if (false && ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      
      // Advanced speech recognition settings for low latency and noise cancellation
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';
      recognitionRef.current.maxAlternatives = 1; // Reduced for faster processing
      
      // Noise cancellation and audio quality settings
      if (recognitionRef.current.audioTrack) {
        recognitionRef.current.audioTrack.applyConstraints({
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000, // Lower sample rate for faster processing
          channelCount: 1 // Mono for better processing
        });
      }

      recognitionRef.current.onstart = () => {
        setIsListening(true);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current.onresult = (event) => {
        // Prevent processing if microphone is muted during AI speech
        if (microphoneMuted || isSpeaking) {
          console.log('🔇 Ignoring speech input - microphone muted or AI speaking');
          return;
        }
        
        let finalTranscript = '';
        let interimTranscript = '';
        let confidence = 0;
        
        // Process all results for low latency
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          confidence = event.results[i][0].confidence || 0;
          
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }
        
        // Enhanced feedback prevention: Check similarity to recent AI output
        const combinedInput = (finalTranscript + interimTranscript).toLowerCase().trim();
        if (recentAIText && combinedInput.length > 5) {
          const similarity = calculateTextSimilarity(combinedInput, recentAIText);
          if (similarity > 0.6) {
            console.log('🚫 High similarity to recent AI speech detected, ignoring:', combinedInput);
            return;
          }
        }
        
        // Voice 2.0: Check for interruption during AI speech
        if (isSpeaking && voiceInterruptable && (interimTranscript || finalTranscript)) {
          console.log('🛑 Voice interruption detected during AI speech');
          handleVoiceInterruption();
          return;
        }
        
        // Voice 2.0: Process voice commands if in command mode
        if (voiceCommandMode && finalTranscript) {
          handleVoiceCommand(finalTranscript.trim());
          return;
        }
        
        // Update last speech time for silence detection - DISABLED for text-only mode
        if (interimTranscript || finalTranscript) {
          // setLastSpeechTime(Date.now()); // DISABLED - voice features off
          
          // Voice 2.0: Generate voice waveform visualization - DISABLED
          // generateVoiceWaveform(confidence); // DISABLED for text-only mode
        }
        
        // Show interim results for responsiveness with confidence indicator
        if (interimTranscript) {
          const confidenceEmoji = confidence > 0.8 ? '🎯' : confidence > 0.6 ? '🎪' : '📢';
          setCurrentTranscript(`${confidenceEmoji} "${interimTranscript}"`);
          
          // Clear any existing silence timer
          if (silenceTimeoutRef.current) {
            clearTimeout(silenceTimeoutRef.current);
          }
          
          // Voice 2.0: Dynamic silence detection based on speech quality
          const silenceDelay = confidence > 0.8 ? 1000 : 1500; // Shorter delay for clear speech
          silenceTimeoutRef.current = setTimeout(() => {
            if (interimTranscript.trim().length > 5) {
              console.log('🔇 Silence detected, processing interim result:', interimTranscript);
              setCurrentTranscript('🧠 Processing your response...');
              
              try {
                recognitionRef.current.stop();
              } catch (error) {
                console.log('Error stopping recognition:', error);
              }
              
              processVoiceInput(interimTranscript.trim());
            }
          }, silenceDelay);
        }
        
        // Process final results immediately (highest priority)
        if (finalTranscript.trim()) {
          console.log('🎤 Final voice input detected:', finalTranscript);
          setCurrentTranscript('🧠 Processing your response...');
          
          // Clear silence timer since we have final result
          if (silenceTimeoutRef.current) {
            clearTimeout(silenceTimeoutRef.current);
          }
          
          // Stop listening while processing
          try {
            recognitionRef.current.stop();
          } catch (error) {
            console.log('Error stopping recognition:', error);
          }
          
          // Voice 2.0: Process voice input with enhanced context
          processVoiceInputV2(finalTranscript.trim(), confidence);
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        setCurrentTranscript('');
        
        // Auto-restart on certain errors
        if (event.error === 'no-speech' || event.error === 'audio-capture') {
          setTimeout(() => {
            if (autoVoiceMode && !isSpeaking) {
              console.log('Auto-restarting voice recognition after error...');
              startListening();
            }
          }, 2000);
        }
      };
    }
    
    // Initialize Speech Synthesis
    if ('speechSynthesis' in window) {
      setSpeechSynthesis(window.speechSynthesis);
      
      // Load available voices
      const loadVoices = () => {
        const voices = window.speechSynthesis.getVoices();
        console.log('Loaded voices:', voices.map(v => ({ name: v.name, lang: v.lang, localService: v.localService })));
        setAvailableVoices(voices);
        
        // Auto-select the best voice
        const bestVoice = voices.find(voice => 
          voice.name.includes('Samantha') || 
          voice.name.includes('Alex') ||
          voice.name.includes('Google US English') ||
          voice.name.includes('Microsoft Zira') ||
          (voice.lang === 'en-US' && voice.localService)
        ) || voices.find(voice => voice.lang.startsWith('en-'));
        
        if (bestVoice) {
          setSelectedVoice(bestVoice);
          console.log('Auto-selected voice:', bestVoice.name);
        }
      };
      
      // Load voices immediately and on voiceschanged event
      loadVoices();
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }

    // Enable voice features
    setTimeout(() => {
      setVoiceEnabled(true);
    }, 1000);
    
    // Cleanup
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (error) {
          console.log('Error stopping recognition:', error);
        }
      }
      if (speechSynthesis) {
        speechSynthesis.cancel();
      }
      
      // Clear all timers for clean shutdown
      const silenceTimeout = silenceTimeoutRef.current;
      const speechTimeout = speechTimeoutRef.current;
      if (silenceTimeout) {
        clearTimeout(silenceTimeout);
      }
      if (speechTimeout) {
        clearTimeout(speechTimeout);
      }
    };
  }, []);

  // Define resetAssessment function before useEffect that uses it
  const resetAssessment = useCallback(() => {
    setMessages([]);
    setCurrentQuestion(0);
    setIsWaitingForConfirmation(false);
    setAssessmentComplete(false);
    setBusinessPlan(null);
    setShowBusinessPlan(false);
    setInputMessage('');
    setIsLoading(false);
  }, []);

  // Enhanced goal loading and initialization
  useEffect(() => {
    const initializeAIMentor = async () => {
      if (!user || !userGoal) {
        // Reset assessment state to ensure we start fresh
        resetAssessment();
        return;
      }
      
      try {
        // Don't block UI with loading, just initialize in background
        
        // 1. Set basic goal data immediately
        setGoalData(userGoal);
        
        // 2. Initialize empty messages to show welcome section
        setMessages([]);
        resetAssessment();
        
        // 3. Background tasks (don't await these)
        setTimeout(async () => {
          try {
            // Analyze goal category in background
            await analyzeGoalCategory(userGoal.title, userGoal.description);
            
            // Create conversation record in background
            if (userGoal.id) {
              const conversationResult = await conversationsAPI.createConversation(
                user.uid, 
                userGoal.id, 
                {
                  type: '11_question_expert_assessment',
                  totalQuestions: 11
                }
              );
              
              if (conversationResult.success) {
                setConversationId(conversationResult.conversationId);
              }
            }
          } catch (error) {
            console.error('Background initialization error:', error);
          }
        }, 100);
        
      } catch (error) {
        console.error('Error initializing AI Mentor:', error);
        // Fallback to basic initialization
        resetAssessment();
      }
    };
    
    initializeAIMentor();
  }, [userGoal, user, resetAssessment]);

  const handleSendMessage = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (!inputMessage.trim() || isLoading) return;
    
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage.trim(),
      timestamp: Date.now()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    // Handle the structured 11-question assessment flow
    if (!assessmentComplete) {
      await handleAssessmentFlow(userMessage.content);
    } else {
      // Continue with normal conversation after assessment
      await generateAIResponse(userMessage.content);
    }
  };

  const handleAssessmentFlow = async (userInput) => {
    const lowerInput = userInput.toLowerCase();
    
    // Check if user wants to start the assessment
    if (currentQuestion === 0 && (lowerInput.includes('yes') || lowerInput.includes('start') || lowerInput.includes('begin') || lowerInput.includes('ready'))) {
      await startAssessment();
      return;
    }
    
    // If we're waiting for confirmation to move to next question
    if (isWaitingForConfirmation) {
      if (lowerInput.includes('yes') || lowerInput.includes('next') || lowerInput.includes('continue') || lowerInput.includes('move on')) {
        await moveToNextQuestion();
        return;
      } else if (lowerInput.includes('no') || lowerInput.includes('clarify') || lowerInput.includes('explain') || lowerInput.includes('help')) {
        await provideQuestionHelp();
        return;
      }
    }
    
    // Handle natural conversation during assessment
    if (currentQuestion > 0 && currentQuestion <= 11) {
      // Check for explicit continuation commands
      if (lowerInput.includes('next') || lowerInput.includes('continue') || lowerInput.includes('move on') || lowerInput.includes('proceed')) {
        await moveToNextQuestion();
        return;
      }
      // For all other inputs, have a natural conversation
      else {
        await generateConversationalResponse(userInput);
        return;
      }
    }
    
    // Default response for unclear input
    await handleUnclearInput();
  };

  const startAssessment = async () => {
    setCurrentQuestion(1);
    
    // Use expert questions if available, otherwise fallback to default template
    const questions = expertQuestions.length > 0 ? expertQuestions : null;
    
    let questionContent;
    let hasAutoResearch = false;
    
    if (questions && questions[0]) {
      const firstQuestion = questions[0];
      questionContent = `**Question 1/11** - ${goalCategory.toUpperCase()} EXPERTISE

**${firstQuestion.question}**

${firstQuestion.expertFocus ? `*Expert Focus: ${firstQuestion.expertFocus}*` : ''}

Please provide a detailed response. The more specific you are, the better I can tailor your strategic plan.

**Ready to move on?** Say "next" or "continue" when you're satisfied with your response.`;
    } else {
      // Fallback to original template system
      const template = getQuestionTemplate(1, userGoal);
      questionContent = template.content;
      hasAutoResearch = template.hasAutoResearch;
    }
    
    const questionMessage = {
      id: Date.now() + 1,
      type: 'assistant',
      content: questionContent,
      hasAutoResearch: hasAutoResearch,
      timestamp: Date.now()
    };

    setMessages(prev => {
      const newMessages = [...prev, questionMessage];
      // Track the start index for this question's conversation
      setCurrentQuestionStartIndex(newMessages.length - 1);
      return newMessages;
    });
    setIsLoading(false);
  };

  const handleQuestionAnswer = async (userInput) => {
    // Store the answer
    setQuestionAnswers(prev => ({
      ...prev,
      [currentQuestion]: userInput
    }));

    // Immediately move to next question without confirmation
    await moveToNextQuestion();
  };

  const moveToNextQuestion = async () => {
    setIsWaitingForConfirmation(false);
    
    // Summarize the current question's conversation before moving on
    await summarizeCurrentQuestionConversation();
    
    if (currentQuestion >= 11) {
      // All questions completed, generate comprehensive plan
      await generateComprehensivePlan();
      return;
    }
    
    const nextQuestionNumber = currentQuestion + 1;
    setCurrentQuestion(nextQuestionNumber);
    
    // Use expert questions if available, otherwise fallback to default template
    const questions = expertQuestions.length > 0 ? expertQuestions : null;
    
    let questionContent;
    let hasAutoResearch = false;
    
    if (questions && questions[nextQuestionNumber - 1]) {
      const nextQuestion = questions[nextQuestionNumber - 1];
      questionContent = `**Question ${nextQuestionNumber}/11** - ${goalCategory.toUpperCase()} EXPERTISE

**${nextQuestion.question}**

${nextQuestion.expertFocus ? `*Expert Focus: ${nextQuestion.expertFocus}*` : ''}

Please provide a detailed response. The more specific you are, the better I can tailor your strategic plan.

**Ready to move on?** Say "next" or "continue" when you're satisfied with your response.`;
    } else {
      // Fallback to original template system
      const template = getQuestionTemplate(nextQuestionNumber, userGoal);
      questionContent = template.content;
      hasAutoResearch = template.hasAutoResearch;
    }
    
    const questionMessage = {
      id: Date.now() + 1,
      type: 'assistant',
      content: questionContent,
      hasAutoResearch: hasAutoResearch,
      timestamp: Date.now()
    };

    setMessages(prev => {
      const newMessages = [...prev, questionMessage];
      // Track the start index for the new question's conversation
      setCurrentQuestionStartIndex(newMessages.length - 1);
      return newMessages;
    });
    setIsLoading(false);
  };

  const provideQuestionHelp = async () => {
    const helpMessage = {
      id: Date.now() + 1,
      type: 'assistant',
      content: `I'm here to help you with **Question ${currentQuestion}/11**.

**Need clarification?** Let me know what specific part of the question you'd like me to explain further.

**Want to add more to your answer?** Just provide additional details, and I'll incorporate them.

**Ready to move on?** Say "next" or "continue" when you're satisfied with your response.

Take your time - this assessment is designed to create the most effective strategy for your success.`,
      timestamp: Date.now()
    };

    setMessages(prev => [...prev, helpMessage]);
    setIsLoading(false);
  };

  const handleUnclearInput = async () => {
    let responseMessage = '';
    
    if (currentQuestion === 0) {
      responseMessage = `I'm ready to begin your **11-question strategic assessment** for achieving: **${userGoal?.title || 'your goal'}**

**To start**, please respond with "**yes**" or "**let's start**" and I'll ask you the first question.

This focused approach ensures we create the most effective strategy for your success! 🎯`;
    } else if (currentQuestion > 0 && currentQuestion <= 11) {
      responseMessage = `I'm waiting for your response to **Question ${currentQuestion}/11**.

Please provide your answer to this question, and I'll confirm it with you before moving to the next one.

**Need help?** Say "help" or "clarify" if you need more explanation about the question.`;
    } else {
      responseMessage = `I'm here to help you with your strategic assessment. 

**Current Status:** Question ${currentQuestion}/11
**What I need:** ${isWaitingForConfirmation ? 'Your confirmation to move to the next question' : 'Your answer to the current question'}

How can I assist you?`;
    }

    const clarificationMessage = {
          id: Date.now() + 1,
          type: 'assistant',
      content: responseMessage,
          timestamp: Date.now()
        };

    setMessages(prev => [...prev, clarificationMessage]);
    setIsLoading(false);
  };

  const generateComprehensivePlan = async () => {
    setAssessmentComplete(true);
    setIsGeneratingPlan(true);
    setPlanGenerationFailed(false);

    const planGenerationMessage = {
      id: Date.now() + 1,
      type: 'assistant',
      content: `# 🎉 Assessment Complete!

Thank you for completing all **11 strategic questions**! 

**Now generating your comprehensive development plan...**

⚡ Analyzing your responses...
🎯 Creating personalized strategy...
📋 Building implementation roadmap...

*This may take a moment to ensure we create the most effective plan for your success.*`,
          timestamp: Date.now()
        };
        
    setMessages(prev => [...prev, planGenerationMessage]);

    // Generate the plan using AI
    try {
      const comprehensivePlan = await generateAIComprehensivePlan();
      
      const planMessage = {
        id: Date.now() + 2,
            type: 'assistant',
        content: comprehensivePlan,
            timestamp: Date.now()
          };

      setMessages(prev => [...prev, planMessage]);
      
      // Create business plan object
      const businessPlanData = {
        type: 'business_plan',
        goal: userGoal?.title || 'Personal Development',
        description: 'AI-generated comprehensive strategy from 11-question assessment',
        content: comprehensivePlan, // Full markdown content for display
        fullContent: comprehensivePlan, // Backup reference to ensure content preservation
        conversationHistory: messages,
        phases: extractPhasesFromPlan(comprehensivePlan), // Extract phases from AI response
        createdAt: Date.now(),
        assessmentAnswers: questionAnswers, // Include the assessment answers
        assessmentCompleted: true,
        totalQuestions: 11,
        questionsAnswered: Object.keys(questionAnswers).length
      };
      
      // Save the plan to sidebar and initialize phase display
      setBusinessPlan(businessPlanData);
      setVisiblePhases(0);
      setShowFullPlan(false);
      
      // Debug logging
      console.log('Business plan generated with phases:', businessPlanData.phases);
      console.log('Full business plan data:', businessPlanData);
      console.log('Business plan content length:', comprehensivePlan.length);
      console.log('Business plan content preview:', comprehensivePlan.substring(0, 500));

      // Save to Firebase using proper businessPlansAPI if user exists
      if (user && userGoal) {
        try {
          // Import businessPlansAPI
          const { businessPlansAPI } = await import('../firebase');
          
          // Create a conversation record first (simplified)
          const conversationData = {
            messages: messages,
            questionAnswers: questionAnswers,
            type: '11_question_assessment',
            totalQuestions: 11,
            completed: true
          };
          
          // Save business plan using proper API
          const planResult = await businessPlansAPI.createBusinessPlan(
            user.uid, 
            userGoal.id || 'temp-goal-id', 
            'temp-conversation-id', 
            {
              goal: userGoal.title,
              description: userGoal.description,
              content: comprehensivePlan, // Full markdown content
              fullContent: comprehensivePlan, // Backup reference
              phases: businessPlanData.phases, // Use the extracted phases
              type: 'business_plan',
              assessmentAnswers: questionAnswers,
              totalQuestions: 11,
              questionsAnswered: Object.keys(questionAnswers).length,
              conversationHistory: messages,
              createdAt: Date.now()
            }
          );
          
          if (planResult.success) {
            console.log('Business plan saved successfully to Firebase:', planResult.planId);
            // Update the business plan with the Firebase ID
            setBusinessPlan(prev => ({
              ...prev,
              id: planResult.planId,
              firebaseSaved: true
            }));
          } else {
            console.error('Failed to save business plan to Firebase:', planResult.error);
          }
        } catch (firebaseError) {
          console.error('Error saving business plan to Firebase:', firebaseError);
          // Still continue with local state - don't break the user experience
        }
      }

    } catch (error) {
      console.error('Error generating plan:', error);
      setPlanGenerationFailed(true);
      const errorMessage = {
        id: Date.now() + 2,
        type: 'assistant',
        content: 'Plan generation failed — your 11 answers are saved. Tap "Retry plan generation" when you are ready.',
        timestamp: Date.now()
      };

      setMessages(prev => [...prev, errorMessage]);
    }

    setIsGeneratingPlan(false);
      setIsLoading(false);
  };

  const generateAIComprehensivePlan = async () => {
    // Compile conversation summaries for all questions
    const assessmentSummary = Object.entries(questionConversations)
      .map(([questionNum, data]) => {
        return `**Question ${questionNum}: ${data.question}**
Conversation Summary (${data.messageCount} messages): ${data.summary}`;
      })
      .join('\n\n');

    // Fallback to question answers if no conversation summaries are available
    const fallbackSummary = Object.keys(questionConversations).length === 0 
      ? Object.entries(questionAnswers)
          .map(([questionNum, answer]) => {
            const questionIndex = parseInt(questionNum) - 1;
            const expertQuestion = expertQuestions[questionIndex];
            const questionText = expertQuestion ? expertQuestion.question : `Question ${questionNum}`;
            const expertFocus = expertQuestion ? expertQuestion.expertFocus : '';
            
            return `**${questionText}**
${expertFocus ? `Expert Focus: ${expertFocus}` : ''}
Answer: ${answer}`;
          })
          .join('\n\n')
      : assessmentSummary;

    const data = await aiChat({
      task: 'plan-generate',
      messages: [
          {
            role: "system",
            content: `You are a world-class ${goalCategory} expert and strategic development mentor with deep expertise in ${goalCategory} success patterns and strategic planning.

## GOAL DEEP ANALYSIS
**User's Specific Goal:** "${userGoal?.title || 'Personal Development'}"

First, perform a comprehensive analysis of their goal:
1. **Goal Nature**: Identify the core essence, complexity, and domain of this goal
2. **Success Patterns**: What strategies typically work for this type of goal?
3. **Common Pitfalls**: What challenges do people usually face with this goal?
4. **Resource Requirements**: What specific skills, knowledge, or resources are typically needed?
5. **Timeline Realities**: What's a realistic timeline based on the goal's nature?

## PERSONALIZED ASSESSMENT ANALYSIS
Analyze their 11-question responses to understand:
- Their current position relative to their goal
- Their unique strengths and challenges
- Their motivation level and commitment
- Their available resources and constraints
- Their preferred learning and working style
- Hidden opportunities they might not see

**RESPONSE FORMAT:**
Create a comprehensive, integrated business strategy using markdown:

# 🎯 Your Integrated Business Strategy for ${userGoal?.title || 'Development'}

## 🔍 Strategic Goal Analysis
*Deep analysis of what success looks like for this specific goal and why most people fail*

## 🏗️ Your Success Architecture
*The fundamental systems and processes needed for sustainable success*

## 📈 Business Implementation Framework
### Phase 1: Foundation Building (Days 1-30)
*Core infrastructure, skills, and systems setup*
- Weekly milestones with specific deliverables
- Daily action themes (what type of work each day)
- Success metrics and checkpoints

### Phase 2: Market Validation & Growth (Days 31-90)
*Testing, iteration, and initial market engagement*
- Weekly milestones with specific deliverables
- Daily action themes aligned with business development
- Revenue/progress targets and measurement

### Phase 3: Scale & Optimization (Days 91-180)
*Scaling systems and optimizing for sustainable growth*
- Weekly milestones with specific deliverables
- Daily action themes focused on scaling
- Long-term sustainability metrics

## 💰 Integrated Revenue Strategy
*How daily actions directly contribute to revenue generation*

## 🎯 Daily Action Blueprint
*How each day's work connects to weekly milestones and monthly phases*
- Monday: Strategic planning and week setup
- Tuesday-Thursday: Core execution aligned with current phase
- Friday: Review, optimization, and next week preparation
- Weekend: Strategic thinking and planning

## 📊 Unified Success Metrics
*How to measure progress from daily actions to long-term success*

## 🎯 Your Immediate Action Plan (Next 7 Days)
*Specific daily actions that launch your business strategy*
**Day 1:** [Specific action aligned with Phase 1 Week 1]
**Day 2:** [Specific action building on Day 1]
**Day 3:** [Specific action advancing the weekly milestone]
**Day 4:** [Specific action with measurable outcome]
**Day 5:** [Week completion and review action]
**Day 6:** [Strategic planning for Week 2]
**Day 7:** [Preparation and mindset work]

## ⚠️ Critical Success Factors
*The 3-5 things that will make or break your success*

## 📈 Integrated Tracking System
*How daily actions, weekly milestones, and monthly phases connect*

**CRITICAL REQUIREMENTS:**
1. Every daily action must clearly connect to the business strategy
2. Weekly milestones must build toward monthly phase completion
3. All recommendations must be derived from their specific assessment responses
4. The 7-day plan must be precise enough for immediate implementation
5. Success metrics must connect daily progress to long-term goal achievement
6. Integrate revenue-focused actions into every phase
7. Make it actionable, realistic, and tailored to their exact responses
8. Focus on building a path to millions in revenue through systematic execution`
          },
          {
            role: "user",
            content: `Please create a comprehensive development plan based on my 11-question assessment responses:

${fallbackSummary}

Goal: ${userGoal?.title || 'Personal Development'}
Description: ${userGoal?.description || 'Not specified'}`
          }
      ],
      temperature: 0.7,
      max_tokens: 2000
    });
    return data.choices[0].message.content;
  };

  // Voice 2.0: Enhanced text-to-speech with personality and context
  const speakResponse = async (text) => {
    if (speechSynthesis && voiceEnabled && conversationMode) {
      // Cancel any ongoing speech
      speechSynthesis.cancel();
      setIsSpeaking(true);
      setMicrophoneMuted(true); // Mute microphone during AI speech
      
      // CRITICAL: Stop voice recognition to prevent feedback loop
      if (isListening && recognitionRef.current) {
        try {
          recognitionRef.current.stop();
          console.log('🔇 Voice 2.0: Stopped voice recognition and muted microphone to prevent feedback during AI speech');
        } catch (error) {
          console.log('Error stopping recognition during speech:', error);
        }
      }
      
      // Clean the text for speech (remove markdown) and make it more conversational
      const cleanText = text
        .replace(/#{1,6}\s/g, '') // Remove headers
        .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
        .replace(/\*(.*?)\*/g, '$1') // Remove italics
        .replace(/\[(.*?)\]\(.*?\)/g, '$1') // Remove links
        .replace(/`(.*?)`/g, '$1') // Remove code
        .replace(/[•-]\s/g, '') // Remove bullet points
        .replace(/\n/g, '. ') // Replace newlines with pauses
        .replace(/\.\s+\./g, '.') // Remove double periods
        .replace(/\s+/g, ' ') // Normalize spaces
        .replace(/\s*\.\s*\./g, '.') // Fix multiple periods
        .replace(/[✅❌]/g, '') // Remove emoji symbols that might cause issues
        .trim(); // Remove character limit to prevent cutting off
      
      // Split long text into manageable chunks for better speech synthesis
      const maxLength = 300; // Reasonable chunk size
      const textChunks = [];
      
      if (cleanText.length <= maxLength) {
        textChunks.push(cleanText);
      } else {
        // Split by sentences first
        const sentences = cleanText.split(/[.!?]+/).filter(s => s.trim().length > 0);
        let currentChunk = '';
        
        for (const sentence of sentences) {
          if ((currentChunk + sentence + '.').length <= maxLength) {
            currentChunk += sentence + '.';
          } else {
            if (currentChunk) textChunks.push(currentChunk.trim());
            currentChunk = sentence + '.';
          }
        }
        if (currentChunk) textChunks.push(currentChunk.trim());
      }
      
      console.log('Speaking text chunks:', textChunks.length, 'Total length:', cleanText.length);
      console.log('Selected voice for speech:', selectedVoice?.name);
      console.log('Available voices count:', availableVoices.length);
      
      // Wait a moment to ensure voices are loaded
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Speak the text in chunks for better reliability
      let currentChunkIndex = 0;
      
      const speakNextChunk = () => {
        if (currentChunkIndex >= textChunks.length) {
          // All chunks spoken, trigger completion
          setIsSpeaking(false);
          // setCurrentTranscript(''); // Commented out - not needed for text-only
          
          // Extended mute period to prevent feedback
          setTimeout(() => {
            setMicrophoneMuted(false);
            console.log('🎤 Microphone unmuted after AI speech completed');
          }, 2000);
          
          // Clear recent AI text after longer period
          setTimeout(() => {
            setRecentAIText('');
            console.log('🧹 Cleared recent AI text tracking');
          }, 8000);
          
          // Only restart if user explicitly has auto voice mode enabled
          if (autoVoiceMode && recognitionRef.current) {
            setTimeout(() => {
              if (!microphoneMuted) {
                console.log('🔄 Restarting listening after extended speech buffer...');
                startListening();
              }
            }, 2500);
          }
          return;
        }
        
        const chunk = textChunks[currentChunkIndex];
        const utterance = new SpeechSynthesisUtterance(chunk);
        
        // Voice 2.0: Apply optimized settings for full speech
        utterance.rate = 0.8; // Slower rate to prevent cutting
        utterance.pitch = voicePersonality === 'friendly' ? 1.1 : voicePersonality === 'casual' ? 0.9 : 1.0;
        utterance.volume = voiceVolume;
        
        // Get fresh voices list and use selected voice
        const currentVoices = speechSynthesis.getVoices();
        const voiceToUse = selectedVoice || currentVoices.find(voice => 
          voice.name.includes('Samantha') || 
          voice.name.includes('Alex') ||
          voice.name.includes('Google US English') ||
          voice.name.includes('Microsoft Zira') ||
          (voice.lang === 'en-US' && voice.localService)
        ) || currentVoices.find(voice => voice.lang.startsWith('en-'));
        
        if (voiceToUse) {
          utterance.voice = voiceToUse;
          console.log('✅ Using voice for chunk', currentChunkIndex + 1, ':', voiceToUse.name);
        } else {
          console.log('❌ No voice found for chunk', currentChunkIndex + 1);
        }
        
        utterance.onend = () => {
          console.log('Chunk', currentChunkIndex + 1, 'of', textChunks.length, 'completed');
          currentChunkIndex++;
          setTimeout(speakNextChunk, 100); // Small delay between chunks
        };
        
        utterance.onerror = (error) => {
          console.log('Speech error on chunk', currentChunkIndex + 1, ':', error);
          currentChunkIndex++;
          setTimeout(speakNextChunk, 500); // Continue with next chunk on error
        };
        
        console.log('Speaking chunk', currentChunkIndex + 1, ':', chunk);
        speechSynthesis.speak(utterance);
      };
      
      // Track recent AI speech for feedback prevention
      setRecentAIText(cleanText.toLowerCase().slice(0, 100));
      
      // Start speaking the first chunk
      speakNextChunk();
    } else if (autoVoiceMode) {
      // If speech is disabled but auto voice mode is on, still restart listening
      setTimeout(() => {
        startListening();
      }, 1000);
    }
  };

  // Start listening function (ChatGPT style)
  const startListening = async () => {
    if (!recognitionRef.current || isListening || isSpeaking || microphoneMuted) {
      console.log('Cannot start listening:', { hasRecognition: !!recognitionRef.current, isListening, isSpeaking, microphoneMuted });
      return;
    }
    
    try {
      console.log('Starting voice recognition...');
      recognitionRef.current.start();
      setCurrentTranscript('Listening...');
    } catch (error) {
      console.log('Error starting recognition:', error);
      if (error.message.includes('already started')) {
        console.log('Recognition already started, setting state');
        setIsListening(true);
        setCurrentTranscript('Listening...');
      } else {
        // Try again after a short delay
        setTimeout(() => {
          if (autoVoiceMode && !isSpeaking) {
            startListening();
          }
        }, 1000);
      }
    }
  };

  // Stop listening function
  const stopListening = async () => {
    if (!recognitionRef.current || !isListening) return;
    
    try {
      recognitionRef.current.stop();
      setCurrentTranscript('');
    } catch (error) {
      console.log('Error stopping recognition:', error);
      setIsListening(false);
    }
  };

  // Toggle auto voice mode (like ChatGPT's voice toggle)
  const toggleAutoVoiceMode = async () => {
    if (!recognitionRef.current) {
      alert('Voice recognition is not supported in your browser');
      return;
    }
    
    const newAutoMode = !autoVoiceMode;
    setAutoVoiceMode(newAutoMode);
    
    if (newAutoMode) {
      // Turn on auto voice mode
      if (!isSpeaking && !isListening) {
        startListening();
      }
    } else {
      // Turn off auto voice mode
      if (isListening) {
        stopListening();
      }
      if (speechSynthesis) {
        speechSynthesis.cancel();
        setIsSpeaking(false);
      }
    }
  };

  // Voice 2.0: Handle voice interruption during AI speech
  const handleVoiceInterruption = () => {
    console.log('🛑 Voice interruption activated');
    
    // Stop AI speech immediately
    if (speechSynthesis) {
      speechSynthesis.cancel();
      setIsSpeaking(false);
    }
    
    // Clear any streaming
    if (streamingTimeoutRef.current) {
      clearTimeout(streamingTimeoutRef.current);
    }
    // setIsStreamingResponse(false); // Commented out - not needed for text-only
    // setStreamingText(''); // Commented out - not needed for text-only
    
    // Update UI to show interruption
    // setCurrentTranscript('🛑 Interrupted - Ready for your input'); // Commented out - not needed for text-only
    
    // Brief pause then restart listening
    setTimeout(() => {
      if (autoVoiceMode && recognitionRef.current) {
        startListening();
      }
    }, 500);
  };
  
  // Voice 2.0: Generate voice waveform visualization
  const generateVoiceWaveform = (confidence) => {
    // Voice waveform generation disabled for text-only mode
    const newWaveform = [];
    for (let i = 0; i < 10; i++) {
      const height = Math.random() * confidence * 100;
      newWaveform.push(height);
    }
    // setVoiceWaveform(newWaveform); // DISABLED - voice features off
  };
  
  // Voice 2.0: Enhanced voice processing with context
  const processVoiceInputV2 = async (voiceText, confidence) => {
    console.log('📤 Voice 2.0 Processing:', voiceText, 'Confidence:', confidence);
    
    // Prevent feedback loop by checking similarity to recent AI speech
    if (recentAIText && voiceText.toLowerCase().includes(recentAIText.slice(0, 50))) {
      console.log('🚫 Rejected potential feedback:', voiceText);
      return;
    }
    
    // Enhanced feedback prevention: ignore if too similar to common AI phrases
    const aiPhrases = [
      'welcome to your personal ai mentor',
      'lets build your complete success strategy',
      'ill help you create a comprehensive business plan',
      'ready to begin',
      'your ai mentor',
      'strategic insights',
      'implementation plan',
      'business strategy',
      'success metrics',
      'immediate actions',
      'question',
      'questions',
      'development plan',
      'comprehensive',
      'personalized',
      'evidence based',
      'ask you',
      'strategic question',
      'next question',
      'mentor',
      'dedicated',
      'thousands of successful'
    ];
    
    const lowerInput = voiceText.toLowerCase();
    if (aiPhrases.some(phrase => lowerInput.includes(phrase))) {
      console.log('🚫 Rejected AI phrase feedback:', voiceText);
      return;
    }
    
    // Voice 2.0: Add to conversation context
    const contextEntry = {
      timestamp: Date.now(),
      text: voiceText,
      confidence: confidence,
      type: 'user'
    };
    setConversationContext(prev => [...prev.slice(-10), contextEntry]); // Keep last 10 entries
    
    // Create user message with confidence indicator
    const confidenceEmoji = confidence > 0.8 ? '🎯' : confidence > 0.6 ? '🎪' : '📢';
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: `${confidenceEmoji} ${voiceText}`,
      timestamp: Date.now(),
      confidence: confidence
    };
    
    // Add to chat immediately
    console.log('💬 Adding voice message to chat:', userMessage);
    setMessages(prev => {
      const newMessages = [...prev, userMessage];
      console.log('✅ Chat updated, total messages:', newMessages.length);
      return newMessages;
    });
    
    // Set input and trigger AI response
    setInputMessage(voiceText);
    setIsLoading(true);
    
    // Voice 2.0: Process AI response with streaming if enabled
    if (voiceStreamingEnabled) {
      await generateStreamingAIResponse(voiceText);
    } else {
      setTimeout(async () => {
        await generateAIResponse(voiceText);
      }, 100);
    }
  };
  
  // Voice 2.0: Handle voice commands for navigation
  const handleVoiceCommand = (command) => {
    const lowerCommand = command.toLowerCase();
    console.log('🎙️ Voice command received:', command);
    
    // Navigation commands
    if (lowerCommand.includes('go back') || lowerCommand.includes('back')) {
      setCurrentTranscript('🔄 Going back...');
      setTimeout(() => onBack(), 1000);
      return;
    }
    
    if (lowerCommand.includes('start over') || lowerCommand.includes('restart')) {
      setCurrentTranscript('🔄 Starting over...');
      setTimeout(() => onBack('start-over'), 1000);
      return;
    }
    
    if (lowerCommand.includes('complete') || lowerCommand.includes('finish')) {
      setCurrentTranscript('✅ Completing setup...');
      console.log('Voice command onComplete - businessPlan:', businessPlan);
      setTimeout(() => onComplete(messages, businessPlan), 1000);
      return;
    }
    
    // Voice mode commands
    if (lowerCommand.includes('exit voice') || lowerCommand.includes('disable voice')) {
      setCurrentTranscript('🔇 Disabling voice mode...');
      setAutoVoiceMode(false);
      setVoiceCommandMode(false);
      return;
    }
    
    if (lowerCommand.includes('command mode')) {
      setCurrentTranscript('🎙️ Voice command mode activated');
      setVoiceCommandMode(true);
      return;
    }
    
    // Quick actions
    if (lowerCommand.includes('start questions') || lowerCommand.includes('begin questions')) {
      setCurrentTranscript('🚀 Starting strategy questions...');
      processVoiceInputV2("Yes, let's start the questions", 1.0);
      return;
    }
    
    if (lowerCommand.includes('create plan') || lowerCommand.includes('generate plan')) {
      setCurrentTranscript('📋 Creating development plan...');
      processVoiceInputV2("I've answered all 11 questions, please create my comprehensive development plan now", 1.0);
      return;
    }
    
    // Default: treat as regular input
    setCurrentTranscript('💬 Processing as conversation...');
    processVoiceInputV2(command, 1.0);
  };
  
  // Legacy function for backward compatibility
  const processVoiceInput = async (voiceText) => {
    await processVoiceInputV2(voiceText, 1.0);
  };

  // Voice 2.0: Generate streaming AI response for real-time conversation
  const generateStreamingAIResponse = async (userInput) => {
    try {
      // setIsStreamingResponse(true); // Commented out - not needed for text-only
      
      // Use conversation context for better responses
      const contextHistory = conversationContext.slice(-5).map(ctx => 
        `${ctx.type === 'user' ? 'User' : 'Assistant'}: ${ctx.text}`
      ).join('\n');
      
      // Enhanced conversation context with voice personality
      const personalityPrompt = getPersonalityPrompt(voicePersonality);
      
      const response = await aiChatStream({
        task: 'mentor-chat',
        messages: [
            {
              role: "system",
              content: `${MENTOR_SYSTEM_PREAMBLE}

# AI Business Mentor - Personal Success Coach

${personalityPrompt}

You are a dedicated AI mentor who has analyzed thousands of successful career transitions. Your role is to synthesize the best strategies from top performers, researchers, and industry leaders to create personalized roadmaps.

## DEEP GOAL ANALYSIS REQUIRED

User's Goal: "${userGoal?.title || 'Career Success & Business Development'}"

BEFORE asking any questions, you must first ANALYZE this goal deeply:

1. **Goal Complexity**: Is this a simple skill goal, complex business venture, personal transformation, or multi-faceted life change?
2. **Hidden Motivations**: What underlying needs, fears, or desires might drive this goal?
3. **Success Factors**: What are the key elements that will determine success for THIS specific goal?
4. **Unique Challenges**: What obstacles are likely specific to this type of goal?
5. **Timeline Implications**: What does the goal suggest about urgency, scope, and realistic timelines?
6. **Resource Requirements**: What skills, knowledge, connections, or capital will be needed?
7. **Personal Context**: What kind of person typically pursues this goal and what might their background be?

## PERSONALIZED QUESTIONING APPROACH

Based on your analysis, create questions that are:
- **GOAL-SPECIFIC**: Tailored to the exact nature of their goal
- **INSIGHT-DRIVEN**: Designed to uncover deeper motivations and constraints
- **STRATEGIC**: Focused on the most critical success factors for their specific situation
- **PROGRESSIVE**: Building on previous answers to go deeper

CORE APPROACH:
- First, acknowledge and analyze their specific goal with genuine insight
- Ask questions that show you understand the unique challenges of their goal
- Provide context-aware guidance that demonstrates deep goal comprehension
- Build questions that reveal the person behind the goal
- Create precise, actionable plans based on their specific situation

CONVERSATION STYLE:
- **INSIGHTFUL** - demonstrate deep understanding of their specific goal
- **STRATEGIC** - focus on the most critical success factors for their situation
- **EMPATHETIC** - acknowledge the personal significance of their goal
- **AUTHORITATIVE** - provide expert guidance specific to their domain
- **PROGRESSIVE** - build deeper understanding with each interaction

Voice Context:
${contextHistory}

If this is the start of a conversation, begin by demonstrating your understanding of their specific goal, then introduce the strategic questioning approach. Otherwise, continue with deep, personalized coaching based on their unique situation.`
            },
            {
              role: "user",
              content: userInput
            }
        ],
        temperature: 0.9, // Higher creativity for natural conversation
        max_tokens: 200, // Slightly longer for streaming
      });

      // Process streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullResponse = '';
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const jsonStr = line.slice(6);
            if (jsonStr.trim() === '[DONE]') continue;
            
            try {
              const data = JSON.parse(jsonStr);
              const content = data.choices?.[0]?.delta?.content;
              
              if (content) {
                fullResponse += content;
                // setStreamingText(fullResponse); // Commented out - not needed for text-only
                
                // Voice 2.0: Stream speech in chunks for immediate response
                if (autoVoiceMode && fullResponse.length > 50 && fullResponse.includes('.')) {
                  const sentences = fullResponse.split('.');
                  if (sentences.length > 1) {
                    const completeSentence = sentences[0] + '.';
                    if (completeSentence.trim()) {
                      speakStreamingText(completeSentence);
                      fullResponse = sentences.slice(1).join('.');
                    }
                  }
                }
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }
      
      // Add final response to chat
      const aiResponse = {
        id: Date.now() + 1,
        type: 'assistant',
        content: fullResponse,
        timestamp: Date.now(),
        streamed: true
      };
      
      // Add to conversation context
      const contextEntry = {
        timestamp: Date.now(),
        text: fullResponse,
        type: 'assistant'
      };
      setConversationContext(prev => [...prev.slice(-10), contextEntry]);
      
      console.log('🤖 Adding streaming AI response to chat');
      setMessages(prev => [...prev, aiResponse]);
      setRetryCount(0);
      
    } catch (error) {
      console.error('Error generating streaming AI response:', error);
      
      const errorResponse = {
        id: Date.now() + 1,
        type: 'assistant',
        content: "I'm experiencing a technical issue. Please try your message again, and I'll provide you with comprehensive guidance for achieving your goals.",
        timestamp: Date.now()
      };
      
      setMessages(prev => [...prev, errorResponse]);
    } finally {
      setIsLoading(false);
      // setIsStreamingResponse(false); // Commented out - not needed for text-only
      // setStreamingText(''); // Commented out - not needed for text-only
      setInputMessage('');
    }
  };
  
  // Voice 2.0: Get personality-based prompt
  const getPersonalityPrompt = (personality) => {
    switch (personality) {
      case 'friendly':
        return "You are warm, encouraging, and supportive. Use friendly language and show enthusiasm for the user's goals.";
      case 'casual':
        return "You are relaxed and conversational. Use casual language, contractions, and speak like a knowledgeable friend.";
      case 'professional':
      default:
        return "You are professional yet approachable. Provide expert guidance while maintaining a warm, human touch.";
    }
  };
  
  // Voice 2.0: Stream speech in real-time
  const speakStreamingText = (text) => {
    if (speechSynthesis && voiceEnabled && !isSpeaking) {
      setIsSpeaking(true);
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = voiceSpeechRate;
      utterance.pitch = 1.0;
      utterance.volume = voiceVolume;
      
      // Apply voice selection
      if (selectedVoice) {
        utterance.voice = selectedVoice;
      }
      
      utterance.onend = () => {
        setIsSpeaking(false);
      };
      
      speechSynthesis.speak(utterance);
    }
  };
  
  // Expert AI Analysis Functions
  const analyzeGoalCategory = async (goal, description) => {
    try {
      const data = await aiChat({
        task: 'plan-questions',
        messages: [
            {
              role: "system",
              content: `You are an expert business strategist and career coach. Analyze the user's goal and determine the most appropriate field/category and expertise level needed.

CATEGORIES TO CHOOSE FROM:
- technology: Software development, AI, tech startups, engineering
- business: Entrepreneurship, business development, sales, marketing
- creative: Arts, design, writing, content creation, media
- health: Fitness, wellness, healthcare, nutrition
- education: Teaching, training, academic pursuits
- finance: Investment, accounting, financial planning, fintech
- professional: Career advancement, leadership, corporate growth
- personal: Life skills, relationships, personal development

Respond with ONLY the category name (one word) that best fits the goal.`
            },
            {
              role: "user",
              content: `Goal: ${goal}\nDescription: ${description}`
            }
        ],
        max_tokens: 10,
        temperature: 0.1
      });
      const category = data.choices[0].message.content.trim().toLowerCase();
      setGoalCategory(category);
      await generateExpertQuestions(goal, description, category);
    } catch (error) {
      console.error('Error analyzing goal category:', error);
      setGoalCategory('general');
    }
  };

  const generateExpertQuestions = async (goal, description, category) => {
    try {
      const data = await aiChat({
        task: 'plan-questions',
        messages: [
            {
              role: "system",
              content: `You are a world-class expert in ${category}. Create 11 strategic assessment questions specifically tailored to achieving success in this field.

REQUIREMENTS:
1. Questions must be highly specific to ${category} field expertise
2. Each question should reveal critical success factors for this domain
3. Questions should uncover resources, skills, network, and strategic positioning
4. Include industry-specific terminology and insights
5. Focus on actionable intelligence that an expert would need to know
6. Questions should build a complete strategic profile for success

Return ONLY a JSON array of 11 question objects in this exact format:
[
  {
    "id": 1,
    "question": "Expert question text here",
    "type": "text",
    "expertFocus": "What this reveals about their strategic position"
  }
]

Make questions progressively deeper and more strategic.`
            },
            {
              role: "user",
              content: `Generate expert ${category} questions for:\nGoal: ${goal}\nDescription: ${description}`
            }
        ],
        max_tokens: 2000,
        temperature: 0.7
      });
      try {
        const questions = JSON.parse(data.choices[0].message.content);
        setExpertQuestions(questions);
      } catch (parseError) {
        console.error('Error parsing expert questions:', parseError);
        // Fallback to default questions
        setExpertQuestions([]);
      }
    } catch (error) {
      console.error('Error generating expert questions:', error);
      setExpertQuestions([]);
    }
  };

  const generateExpertWelcomeMessage = (goal, category) => {
    const categoryExperts = {
      technology: "Senior Technology Strategist & Engineering Leader",
      business: "Business Development Expert & Startup Advisor", 
      creative: "Creative Director & Industry Professional",
      health: "Health & Wellness Coach",
      education: "Educational Leadership Consultant",
      finance: "Financial Strategy Advisor",
      professional: "Executive Career Coach",
      personal: "Life Strategy Coach"
    };

    const expert = categoryExperts[category] || "Strategic Development Coach";

    return `# 🎯 Welcome to Your Expert Assessment

I'm your **${expert}**, and I've analyzed your goal: **"${goal}"**

## What Makes This Different
✨ **Field-Specific Expertise** - Questions tailored specifically for ${category} success
🎯 **Strategic Intelligence** - Each question reveals critical success factors
📈 **Proven Framework** - Based on patterns from high achievers in ${category}

## Your Personalized Assessment
I'll ask you **11 expert-level questions** designed specifically for ${category} goals. These aren't generic questions - they're the exact strategic insights I'd gather if you hired me as your personal ${category} consultant.

**Ready to begin your expert assessment?**

Type "**start**" or "**let's go**" to begin with your first strategic question.`;
  };

  // Regular AI response for conversation after assessment is complete
  const generateAIResponse = async (userInput) => {
    try {
      const conversationHistory = messages.map(msg => 
        `${msg.type === 'user' ? 'User' : 'Assistant'}: ${msg.content}`
      ).join('\n\n');

      const data = await aiChat({
        task: 'mentor-chat',
        messages: [
            {
              role: "system",
              content: `${MENTOR_SYSTEM_PREAMBLE}

You are a dedicated AI mentor continuing a conversation after completing a comprehensive 11-question assessment.

The user has already completed their assessment and received their comprehensive development plan. Now you're providing ongoing mentorship and support.

**User's Goal:** ${userGoal?.title || 'Personal Development'}

**Assessment Context:**
The user has completed all 11 strategic questions and received their comprehensive development plan. They now have a clear roadmap for success.

**Your Role:**
- Provide ongoing support and clarification
- Help with implementation of their plan
- Answer specific questions about their strategy
- Offer motivation and accountability
- Adapt the plan as needed based on their progress

**Previous Conversation:**
${conversationHistory}

Be supportive, specific, and focused on helping them implement their personalized strategy.`
            },
            {
              role: "user",
              content: userInput
            }
        ],
        temperature: 0.7,
        max_tokens: 500
      });
        const aiResponse = {
          id: Date.now() + 1,
          type: 'assistant',
        content: data.choices[0].message.content,
          timestamp: Date.now()
        };

        setMessages(prev => [...prev, aiResponse]);
    } catch (error) {
      console.error('Error generating AI response:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: 'I encountered a technical issue. Please try your message again, and I\'ll provide you with comprehensive guidance.',
        timestamp: Date.now()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle quick action button clicks
  // Helper functions for deep goal analysis
  const getGoalInsight = (goalText) => {
    if (!goalText) return "this represents a significant personal development opportunity.";
    
    const goal = goalText.toLowerCase();
    
    if (goal.includes('business') || goal.includes('startup') || goal.includes('entrepreneur')) {
      return "this involves building something from the ground up, which requires both strategic thinking and execution excellence.";
    } else if (goal.includes('creative') || goal.includes('art') || goal.includes('writing') || goal.includes('design')) {
      return "this combines creative expression with practical skill development, requiring both artistic vision and market understanding.";
    } else if (goal.includes('skill') || goal.includes('learn') || goal.includes('programming') || goal.includes('language')) {
      return "this represents a commitment to continuous learning and skill mastery, which compounds over time.";
    } else if (goal.includes('fitness') || goal.includes('health') || goal.includes('weight')) {
      return "this involves transforming both physical habits and mental discipline, creating lasting lifestyle changes.";
    } else if (goal.includes('career') || goal.includes('job') || goal.includes('promotion')) {
      return "this requires strategic positioning and skill development in a competitive environment.";
    } else {
      return "this represents a meaningful personal transformation that will require strategic planning and consistent action.";
    }
  };
  
  const getGoalSpecificInsights = (goalText) => {
    if (!goalText) return "- This transformation requires an integrated approach connecting daily actions to long-term vision\n- Success depends on building sustainable systems, not just motivation\n- Your progress will compound rapidly once the right foundation is established";
    
    const goal = goalText.toLowerCase();
    
    if (goal.includes('business') || goal.includes('startup') || goal.includes('entrepreneur')) {
      return "- Revenue generation must be integrated into your daily workflow from day one\n- Market validation happens through systematic customer engagement, not assumptions\n- Your daily actions will focus on building systems that scale, not just completing tasks\n- Success requires treating each day's work as a business investment with measurable ROI";
    } else if (goal.includes('creative') || goal.includes('art') || goal.includes('writing') || goal.includes('design')) {
      return "- Creative mastery and commercial success require parallel development, not sequential\n- Daily creation combined with audience building creates sustainable creative careers\n- Your schedule will balance skill development with market engagement\n- Success means building a creative business, not just improving creative skills";
    } else if (goal.includes('skill') || goal.includes('learn') || goal.includes('programming') || goal.includes('language')) {
      return "- Skill acquisition accelerates when immediately applied to real-world projects\n- Daily practice sessions will be designed for deliberate improvement, not just repetition\n- Your learning path will connect each day's study to practical application opportunities\n- Success requires building a portfolio of applied knowledge, not just theoretical understanding";
    } else if (goal.includes('fitness') || goal.includes('health') || goal.includes('weight')) {
      return "- Physical transformation requires integrated lifestyle design, not just exercise routines\n- Daily habits will be systematically optimized for sustainable progress\n- Your approach will address mindset, environment, and systems simultaneously\n- Success means creating a lifestyle that naturally maintains your results";
    } else if (goal.includes('career') || goal.includes('job') || goal.includes('promotion')) {
      return "- Career advancement requires strategic positioning combined with skill demonstration\n- Daily actions will focus on building visible value and strategic relationships\n- Your approach will treat career development as a business growth strategy\n- Success means becoming indispensable while expanding your opportunities";
    } else {
      return "- This goal requires a systematic approach where daily actions compound toward transformation\n- Success depends on building integrated systems that support long-term achievement\n- Your plan will connect immediate actions to long-term vision through measurable milestones\n- Progress will accelerate as you establish momentum through consistent, strategic action";
    }
  };
  
  const getGoalQuestioningStrategy = (goalText) => {
    if (!goalText) return "I'll understand your situation to create an integrated strategy where daily actions build systematically toward your transformation.";
    
    const goal = goalText.toLowerCase();
    
    if (goal.includes('business') || goal.includes('startup') || goal.includes('entrepreneur')) {
      return "I'll design questions to understand your business foundation needs so we can create daily workflows that build revenue-generating systems from day one.";
    } else if (goal.includes('creative') || goal.includes('art') || goal.includes('writing') || goal.includes('design')) {
      return "I'll explore how to integrate creative development with audience building so your daily practice simultaneously improves your craft and builds your market.";
    } else if (goal.includes('skill') || goal.includes('learn') || goal.includes('programming') || goal.includes('language')) {
      return "I'll determine how to structure daily learning sessions that immediately apply to real projects, accelerating both skill development and practical results.";
    } else if (goal.includes('fitness') || goal.includes('health') || goal.includes('weight')) {
      return "I'll understand your lifestyle to create daily habits that naturally support your transformation without requiring unsustainable willpower.";
    } else if (goal.includes('career') || goal.includes('job') || goal.includes('promotion')) {
      return "I'll explore how to align daily work activities with strategic career positioning so every day advances your professional objectives.";
    } else {
      return "I'll understand your unique situation to design daily actions that systematically build toward your goal through integrated, compound progress.";
    }
  };


  const getContextualPlaceholder = () => {
    if (isWaitingForConfirmation) {
      return 'Type "yes" to continue or add more details...';
    }
    if (currentQuestion === 0) {
      return 'Type "yes" to start the assessment or ask me anything...';
    }
    if (currentQuestion > 0 && !assessmentComplete) {
      return 'Type your answer to the question above...';
    }
    return 'Ask me anything about achieving your goal...';
  };


  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      
      recognition.onstart = () => {
        setIsRecording(true);
      };
      
      recognition.onresult = (event) => {
        let transcript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            transcript += event.results[i][0].transcript;
          }
        }
        if (transcript) {
          setCurrentTranscript(transcript);
          setInputMessage(prev => prev + ' ' + transcript);
        }
      };
      
      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsRecording(false);
      };
      
      recognition.onend = () => {
        setIsRecording(false);
        setCurrentTranscript('');
      };
      
      setSpeechRecognition(recognition);
    }
  }, []);

  // Smart Auto-Voice: DISABLED for text-only assessment
  useEffect(() => {
    // Voice features completely disabled - no auto-voice activation
    console.log('Auto-voice disabled - text-only mode');
  }, []);

  // Action plan creation function (currently disabled for performance optimization)
  const createActionPlan = async () => {
    setIsLoading(true);
    
    // Add a message showing plan generation is in progress
    const generatingMessage = {
      id: Date.now(),
      type: 'assistant',
      content: "I'm creating your personalized professional development plan based on your goal and current situation. This will take a moment...",
      timestamp: Date.now()
    };
    setMessages(prev => [...prev, generatingMessage]);

    try {

      // Get conversation context for the AI
      const conversationHistory = messages.map(msg => 
        `${msg.type === 'user' ? 'User' : 'Assistant'}: ${msg.content}`
      ).join('\n\n');

      const data = await aiChat({
        task: 'plan-generate',
        messages: [
            {
              role: "system",
              content: `You are an elite performance strategist creating a hyper-specific, measurable development plan. Your goal is to create PRECISE, QUANTIFIABLE objectives that leave no room for interpretation.

CRITICAL REQUIREMENTS:
- Every goal must include EXACT numbers, timeframes, and success criteria
- All skills must have specific progressions with measurable benchmarks
- Daily/weekly activities must be precisely defined with durations and quantities
- Resources must include specific names, costs, and exact usage instructions

User's Goal: ${userGoal?.title || 'Professional Development'}
Goal Description: ${userGoal?.description || 'Not specified'}
Previous Conversation Context: ${conversationHistory}

CREATE A PLAN WITH MILITARY-LEVEL PRECISION:

For each phase, provide:

1. **Baseline Assessment** - Exact tests with numerical results
   Example: "Complete 10 consecutive ollies at 70% success rate" NOT "improve basic skills"

2. **SMART Goals** - Specific numbers and deadlines
   Example: "Land 15 kickflips in 20 attempts (75% success rate) by day 30" NOT "learn kickflips"

3. **Core Skills & Drills** - Exact practice routines
   Example: "Practice ollies: 50 attempts, 3 sets, rest 2 minutes between sets, track success rate daily" NOT "practice basic tricks"

4. **Daily Schedule** - Precise time allocations
   Example: "Monday-Friday: 90 minutes practice (30min warm-up, 45min skill work, 15min cool-down)" NOT "practice regularly"

5. **Weekly Metrics** - Exact measurement criteria
   Example: "Week 2 target: Complete 8/10 rail slides, hold manual for 15 seconds, 40 successful ollies/hour" NOT "improve consistency"

6. **Equipment & Costs** - Specific items and prices
   Example: "Element 7.75" deck ($45), Independent trucks ($60), Bones Reds bearings ($15)" NOT "get proper equipment"

7. **Physical Conditioning** - Exact exercise protocols
   Example: "100 squats, 50 calf raises, 3x30-second single-leg balance holds daily" NOT "improve fitness"

8. **Progress Tracking** - Specific measurement methods
   Example: "Film 10 trick attempts daily, log success rate in spreadsheet, measure improvement weekly" NOT "track progress"

RESPONSE FORMAT - Return as valid JSON with these exact fields:

{
  "goal": "${userGoal?.title}",
  "description": "${userGoal?.description}",
  "createdAt": ${Date.now()},
  "phases": [
    {
      "title": "Phase 1: [Specific Phase Name] (Days 1-60)",
      "subtitle": "[Exact outcome description]",
      "dailyCommitment": "[Exact time per day: X hours Y minutes]",
      "weeklyTarget": "[Specific weekly achievement with numbers]",
      "specificGoals": [
        "[Goal 1: Exact skill + number + timeframe]",
        "[Goal 2: Exact measurement + success criteria]",
        "[Goal 3: Specific milestone + deadline]"
      ],
      "dailyRoutine": {
        "warmUp": "[Exact 10-15 minute routine with specific exercises]",
        "skillPractice": "[Exact 30-45 minute practice with specific drills]",
        "conditioning": "[Exact 15-20 minute workout with reps/sets]",
        "coolDown": "[Exact 5-10 minute routine]"
      },
      "weeklySchedule": [
        "Monday: [Exact activities with time allocations]",
        "Tuesday: [Exact activities with time allocations]",
        "Wednesday: [Exact activities with time allocations]",
        "Thursday: [Exact activities with time allocations]",
        "Friday: [Exact activities with time allocations]",
        "Saturday: [Exact activities with time allocations]",
        "Sunday: [Rest or specific light activity]"
      ],
      "measurableSkills": [
        {
          "skill": "[Exact skill name]",
          "week1Target": "[Specific number/percentage]",
          "week4Target": "[Specific number/percentage]",
          "week8Target": "[Specific number/percentage]",
          "measurementMethod": "[Exact way to measure progress]"
        }
      ],
      "equipment": [
        {
          "item": "[Exact product name]",
          "cost": "[Exact price]",
          "where": "[Specific store/website]",
          "priority": "[Essential/Recommended/Optional]"
        }
      ],
      "weeklyMilestones": [
        "Week 1: [Specific achievement with numbers]",
        "Week 2: [Specific achievement with numbers]",
        "Week 3: [Specific achievement with numbers]",
        "Week 4: [Specific achievement with numbers]",
        "Week 5: [Specific achievement with numbers]",
        "Week 6: [Specific achievement with numbers]",
        "Week 7: [Specific achievement with numbers]",
        "Week 8: [Specific achievement with numbers]"
      ],
      "assessmentTests": [
        "[Test 1: Exact challenge with pass/fail criteria]",
        "[Test 2: Exact measurement with target score]",
        "[Test 3: Exact skill demonstration with success rate]"
      ],
      "dayRange": "1-60",
      "successCriteria": "[Exact requirements to pass this phase]"
    }
  ],
  "overallTarget": "[Exact 6-month achievement with specific numbers]",
  "dailyTimeCommitment": "[Exact daily time requirement]",
  "totalBudget": "[Exact cost breakdown]"
}

EXAMPLES OF GOOD VS BAD SPECIFICITY:

BAD: "Improve skateboarding skills"
GOOD: "Land 20 consecutive ollies while moving at 5mph"

BAD: "Practice regularly"  
GOOD: "2 hours daily: 30min balance drills, 60min trick practice, 30min physical conditioning"

BAD: "Get better at writing"
GOOD: "Write 1,500 words daily, complete 3 short stories per month, submit to 5 publications weekly"

BAD: "Learn programming"
GOOD: "Complete 50 coding challenges on LeetCode, build 3 full-stack apps, contribute to 10 open-source repositories"

CRITICAL: Return ONLY valid JSON with NO markdown, NO explanations, NO trailing commas. The response must be parseable by JSON.parse().`
            },
            {
              role: "user",
              content: `Create a comprehensive professional development plan for achieving: ${userGoal?.title}. Description: ${userGoal?.description}. Make it specific, actionable, and tailored to my situation based on our conversation.`
            }
        ],
        temperature: 0.7,
        max_tokens: 1500
      });
      if (data.choices && data.choices[0]) {
        try {
          // Parse the JSON response from AI
          const planContent = data.choices[0].message.content.trim();
          let plan;
          
          // Multiple parsing strategies
          try {
            // Strategy 1: Direct parse
            plan = JSON.parse(planContent);
          } catch (directParseError) {
            try {
              // Strategy 2: Extract JSON with regex
              const jsonMatch = planContent.match(/\{[\s\S]*\}/);
              if (jsonMatch) {
                plan = JSON.parse(jsonMatch[0]);
              } else {
                throw new Error('No JSON found in response');
              }
            } catch (regexParseError) {
              try {
                // Strategy 3: Clean and fix common JSON issues
                let cleanedContent = planContent
                  .replace(/,\s*}/g, '}')  // Remove trailing commas in objects
                  .replace(/,\s*]/g, ']')  // Remove trailing commas in arrays
                  .replace(/\n/g, ' ')     // Replace newlines with spaces
                  .replace(/\t/g, ' ')     // Replace tabs with spaces
                  .replace(/\s+/g, ' ')    // Normalize whitespace
                  .trim();
                
                // Extract JSON if wrapped in text
                const cleanJsonMatch = cleanedContent.match(/\{[\s\S]*\}/);
                if (cleanJsonMatch) {
                  cleanedContent = cleanJsonMatch[0];
                }
                
                plan = JSON.parse(cleanedContent);
              } catch (cleanParseError) {
                // Strategy 4: Create fallback plan structure
                console.error('All JSON parsing strategies failed, creating fallback plan');
                plan = {
                  goal: userGoal?.title || 'Professional Development',
                  description: userGoal?.description || 'AI-generated development plan',
                  createdAt: Date.now(),
                  phases: [
                    {
                      title: "Phase 1: Foundation (Days 1-60)",
                      subtitle: "Building core skills and baseline assessment",
                      smartGoals: ["Establish daily practice routine", "Complete baseline assessment"],
                      coreSkills: ["Focus on fundamental skill development", "Practice 1-2 hours daily"],
                      resources: ["Identify free learning resources", "Set up practice environment"],
                      focus: "Building foundation and establishing consistent practice",
                      dayRange: "1-60",
                      keyMetric: "Consistent daily practice and baseline completion"
                    },
                    {
                      title: "Phase 2: Development (Days 61-120)",
                      subtitle: "Skill building and competency development",
                      smartGoals: ["Achieve intermediate proficiency", "Build project portfolio"],
                      coreSkills: ["Advanced skill practice", "Real-world application"],
                      resources: ["Utilize online communities", "Seek feedback from peers"],
                      focus: "Developing competency and practical application",
                      dayRange: "61-120",
                      keyMetric: "Demonstrable skill improvement and project completion"
                    },
                    {
                      title: "Phase 3: Mastery (Days 121-180)",
                      subtitle: "Advanced skills and professional application",
                      smartGoals: ["Achieve professional-level competency", "Launch or apply skills"],
                      coreSkills: ["Master-level practice", "Teaching or mentoring others"],
                      resources: ["Professional networking", "Monetization strategies"],
                      focus: "Achieving mastery and professional application",
                      dayRange: "121-180",
                      keyMetric: "Professional-level performance and practical results"
                    }
                  ]
                };
              }
            }
          }
          
          // Validate plan structure
          if (!plan || !plan.phases || !Array.isArray(plan.phases)) {
            throw new Error('Invalid plan structure');
          }
          
          // Add conversation history to the plan
          plan.conversationHistory = messages.map(msg => ({
            id: msg.id,
            type: msg.type,
            content: msg.content,
            timestamp: msg.timestamp
          }));
          
          setBusinessPlan(plan);
          
          // Remove generating message and add success message
          setMessages(prev => prev.filter(msg => msg.id !== generatingMessage.id));
          
          const successMessage = {
            id: Date.now() + 1,
            type: 'assistant',
            content: `## 🎉 Your Personalized Development Plan is Ready!

I've created your **comprehensive professional development plan** with business integration! It's displayed in the sidebar and covers **${plan.phases?.length || 3} strategic phases** over the next 6 months.

### What's Included:
• **Specific tasks** with exact timelines and success metrics
• **Business strategies** for monetizing your "${plan.goal}" skills
• **Daily routines** with precise time allocations
• **Weekly milestones** with measurable targets
• **Resource recommendations** with costs and sources

### Next Steps:
The plan is now **saved** and will be available in your daily journal for tracking progress. 

**Would you like me to:**
• Explain any specific phase in detail?
• Help you get started with the first steps?
• Discuss the business strategy components?
• Provide additional resources for any area?`,
            timestamp: Date.now()
          };
          setMessages(prev => [...prev, successMessage]);
          
          // Save to Firebase
          if (user) {
            try {
              await set(ref(database, `users/${user.uid}/actionPlan`), plan);
              console.log('AI-generated professional development plan saved successfully');
            } catch (error) {
              console.error('Error saving action plan:', error);
            }
          }
          
          setRetryCount(0);
        } catch (parseError) {
          console.error('Error parsing AI plan response:', parseError);
          throw new Error('Failed to parse plan response');
        }
      } else {
        throw new Error('No response from AI');
      }
    } catch (error) {
      console.error('Error generating action plan:', error);
      
      // Remove generating message
      setMessages(prev => prev.filter(msg => msg.id !== generatingMessage.id));
      
      // Handle rate limiting with exponential backoff (same as handleSendMessage)
      if (error.message && error.message.includes('429')) {
        const newRetryCount = retryCount + 1;
        setRetryCount(newRetryCount);
        
        const errorMessage = {
          id: Date.now() + 1,
          type: 'assistant',
          content: "I'm experiencing high demand right now. Please try again in a moment and I'll create your personalized development plan.",
          timestamp: Date.now()
        };
        
        setMessages(prev => [...prev, errorMessage]);
        
        return;
      }
      
      // Handle other errors
      const errorMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: "I encountered an issue while creating your professional development plan. Please try the 'Generate Development Plan' button again, and I'll create a comprehensive, personalized plan for achieving your goals.",
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Reference function to avoid lint warning
  if (false) createActionPlan();

  // Dynamic entrepreneurial question templates tailored to user's goal
  const getQuestionTemplate = (questionNumber, userGoal) => {
    const goal = userGoal?.title || 'your business venture';
    
    // Extract key context from goal for personalization
    const getPersonalizedContext = (goalText) => {
      const text = goalText.toLowerCase();
      
      // Career/Role-based goals
      if (text.includes('cto') || text.includes('chief technology officer')) {
        return {
          role: 'CTO',
          industry: text.includes('recruiting') ? 'recruiting' : 'technology',
          context: 'technology leadership role',
          scale: text.includes('billion') ? 'billion-dollar' : 'large-scale'
        };
      }
      
      // Business type detection
      if (text.includes('app') || text.includes('software')) {
        return { context: 'software/app business', industry: 'technology' };
      }
      if (text.includes('recruiting') || text.includes('staffing')) {
        return { context: 'recruiting business', industry: 'staffing' };
      }
      if (text.includes('ecommerce') || text.includes('online store')) {
        return { context: 'ecommerce business', industry: 'retail' };
      }
      
      return { context: 'business venture', industry: 'general' };
    };
    
    const context = getPersonalizedContext(goal);
    
    // Create personalized questions based on context
    const questions = {
      1: { content: `**Question 1/11: Vision & Impact**\n\nWhat specific problem in ${context.industry} are you solving by ${goal}? Who are the key stakeholders who will benefit most from your ${context.context}?`, hasAutoResearch: false },
      2: { content: `**Question 2/11: Your Unique Advantage**\n\nWhat unique skills, connections, or insights do you bring to ${goal}? What makes your approach different from others pursuing similar ${context.industry} opportunities?`, hasAutoResearch: false },
      3: { content: `**Question 3/11: Market Opportunity**\n\nWhat market forces are creating the opportunity for ${goal}? What trends in ${context.industry} support your timing?`, hasAutoResearch: true },
      4: { content: `**Question 4/11: Strategic Approach**\n\nWhat's your step-by-step strategy to achieve ${goal}? What are the key milestones and how will you measure progress?`, hasAutoResearch: false },
      5: { content: `**Question 5/11: Value Creation**\n\nHow will ${goal} create measurable value? What metrics will demonstrate your success in this ${context.context}?`, hasAutoResearch: false },
      6: { content: `**Question 6/11: Network & Positioning**\n\nWhat key relationships do you need to build to achieve ${goal}? How will you position yourself in the ${context.industry} ecosystem?`, hasAutoResearch: false },
      7: { content: `**Question 7/11: Skills & Development**\n\nWhat capabilities do you need to develop to successfully ${goal}? What's your learning and skill-building plan?`, hasAutoResearch: false },
      8: { content: `**Question 8/11: Resource Strategy**\n\nWhat resources (financial, human, technological) do you need to achieve ${goal}? How will you acquire them?`, hasAutoResearch: false },
      9: { content: `**Question 9/11: Risk Management**\n\nWhat are the biggest obstacles to ${goal}? How will you navigate challenges specific to ${context.industry}?`, hasAutoResearch: false },
      10: { content: `**Question 10/11: Scaling Strategy**\n\nOnce you achieve ${goal}, how will you leverage that success? What's your long-term vision for impact in ${context.industry}?`, hasAutoResearch: false },
      11: { content: `**Question 11/11: Personal Drive**\n\nWhat drives your passion for ${goal}? What personal values and motivations will sustain you through the challenges of achieving this ${context.context}?`, hasAutoResearch: false }
    };

    return questions[questionNumber] || { content: `**Question ${questionNumber}/11**`, hasAutoResearch: false };
  };

  // Detect goal type for tailored questions
  const detectGoalType = (goalText) => {
    const text = goalText.toLowerCase();
    if (text.includes('business') || text.includes('startup') || text.includes('company') || text.includes('entrepreneur')) {
      return 'business';
    }
    if (text.includes('creative') || text.includes('art') || text.includes('writing') || text.includes('design') || text.includes('music')) {
      return 'creative';
    }
    if (text.includes('skill') || text.includes('learn') || text.includes('programming') || text.includes('language') || text.includes('development')) {
      return 'skill';
    }
    if (text.includes('fitness') || text.includes('health') || text.includes('weight') || text.includes('exercise') || text.includes('training')) {
      return 'fitness';
    }
    return 'default';
  };

  const handleAutoResearch = async () => {
    setIsLoading(true);
    try {
      const data = await aiChat({
        task: 'plan-questions',
        messages: [
            {
              role: "system",
              content: "You are Delta Technologies' advanced Research Program, specializing in market analysis for entrepreneurial ventures. Provide a comprehensive market analysis including market size, key trends, growth projections, competitive landscape, and opportunities. Base it on real data where possible, and make it relevant to the user's goal."
            },
            {
              role: "user",
              content: `Perform a detailed market analysis for: ${userGoal?.title || 'the business venture'}. Include market size, trends, opportunities, and data. Powered by Delta Technologies Research Program.`
            }
        ],
        temperature: 0.7,
        max_tokens: 1000
      });
      const analysis = data.choices[0].message.content;

      // Submit as user answer
      await handleAssessmentFlow(analysis);
    } catch (error) {
      console.error('Error in auto research:', error);
      const errorMessage = {
        id: Date.now(),
        type: 'assistant',
        content: 'Sorry, there was an error performing the auto research. Please try answering manually.',
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const provideIdeasForQuestion = async () => {
    const currentQ = expertQuestions[currentQuestion - 1] || getQuestionTemplate(currentQuestion, userGoal);
    
    try {
      const data = await aiChat({
        task: 'plan-questions',
        messages: [
            {
              role: "system",
              content: `You are a helpful AI mentor providing ideas and suggestions for the question: "${currentQ.question || currentQ.content}".
              
Provide 3-5 specific, creative ideas or examples to inspire the user's response. Keep it concise and relevant to their goal: "${userGoal?.title}".`
            }
        ],
        temperature: 0.7,
        max_tokens: 300
      });
      const ideas = data.choices[0].message.content;

      const ideasMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: `Here are some ideas to help with Question ${currentQuestion}:
        
${ideas}
        
Feel free to use these as inspiration or provide your own response. When ready, share your answer!`,
        timestamp: Date.now()
      };

      setMessages(prev => [...prev, ideasMessage]);
    } catch (error) {
      console.error('Error generating ideas:', error);
      const errorMsg = {
        id: Date.now() + 1,
        type: 'assistant',
        content: 'Sorry, I couldn\'t generate ideas right now. Please provide your response or ask for help in another way.',
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, errorMsg]);
    }
    
    setIsLoading(false);
  };

  const provideQuestionResources = async () => {
    const currentQ = expertQuestions[currentQuestion - 1] || getQuestionTemplate(currentQuestion, userGoal);
    
    try {
      const data = await aiChat({
        task: 'plan-questions',
        messages: [
            {
              role: "system",
              content: `You are a helpful AI mentor. The user has indicated they don't know how to answer this question: "${currentQ.question || currentQ.content}".

Provide helpful resources, frameworks, or guiding questions to help them understand and think through this topic. Include:
1. Key concepts they should understand
2. Guiding questions to help them reflect
3. Common examples or frameworks relevant to their goal: "${userGoal?.title}"
4. Encourage them to say "next" or "continue" when they're ready to move forward

Be supportive and educational. Help them build understanding before proceeding.`
            }
        ],
        temperature: 0.7,
        max_tokens: 500
      });
      const resources = data.choices[0].message.content;

      const resourceMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: `I understand this question might be challenging. Let me help you build some understanding:

${resources}

Take your time to reflect on these points. When you feel ready to continue to the next question, just say **"next"** or **"continue"**.`,
        timestamp: Date.now()
      };

      setMessages(prev => [...prev, resourceMessage]);
    } catch (error) {
      console.error('Error generating resources:', error);
      const errorMsg = {
        id: Date.now() + 1,
        type: 'assistant',
        content: `I understand this question might be unclear. Let me help you think through it:

**Let's break down this question step by step:**
- Think about your current situation with "${userGoal?.title}"
- Consider what resources, skills, or knowledge you already have
- Reflect on what might be missing or what you'd like to develop

Don't worry if you're not sure - this is all part of the learning process. When you're ready to move forward, just say **"next"** or **"continue"**.`,
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, errorMsg]);
    }
    
    setIsLoading(false);
  };

  const summarizeCurrentQuestionConversation = async () => {
    try {
      // Get the conversation messages for the current question
      const conversationMessages = messages.slice(currentQuestionStartIndex);
      
      if (conversationMessages.length <= 1) {
        // No conversation to summarize (just the question itself)
        return;
      }

      // Extract the question text
      const currentQ = expertQuestions[currentQuestion - 1] || getQuestionTemplate(currentQuestion, userGoal);
      const questionText = currentQ.question || currentQ.content;

      // Build conversation for summarization
      const conversationText = conversationMessages.map(msg => 
        `${msg.type === 'user' ? 'User' : 'Assistant'}: ${msg.content}`
      ).join('\n\n');

      const data = await aiChat({
        task: 'plan-summary',
        messages: [
            {
              role: "system",
              content: `You are summarizing a conversation between a user and AI mentor about a specific assessment question.

QUESTION: "${questionText}"

TASK: Create a concise summary that captures:
1. The user's key insights, responses, and perspectives
2. Important clarifications or additional context discussed
3. The essence of their answer to the question
4. Any notable concerns, challenges, or opportunities mentioned

Keep the summary focused and factual. This will be used to generate a comprehensive business plan later.

CONVERSATION TO SUMMARIZE:
${conversationText}`
            }
        ],
        temperature: 0.5,
        max_tokens: 300
      });
      const summary = data.choices[0].message.content;

      // Store the summary for this question
      setQuestionConversations(prev => ({
        ...prev,
        [currentQuestion]: {
          question: questionText,
          summary: summary,
          messageCount: conversationMessages.length - 1 // Exclude the question itself
        }
      }));

    } catch (error) {
      console.error('Error summarizing conversation:', error);
      
      // Fallback: Store basic information without AI summarization
      const currentQ = expertQuestions[currentQuestion - 1] || getQuestionTemplate(currentQuestion, userGoal);
      const questionText = currentQ.question || currentQ.content;
      
      setQuestionConversations(prev => ({
        ...prev,
        [currentQuestion]: {
          question: questionText,
          summary: `User discussed this question through ${messages.slice(currentQuestionStartIndex).length - 1} messages. Full conversation context available for business plan generation.`,
          messageCount: messages.slice(currentQuestionStartIndex).length - 1
        }
      }));
    }
  };

  const generateConversationalResponse = async (userInput) => {
    try {
      // Build conversation history for context
      const conversationHistory = messages.map(msg => ({
        role: msg.type === 'user' ? 'user' : 'assistant',
        content: msg.content
      }));

      // Get current question context
      const currentQ = expertQuestions[currentQuestion - 1] || getQuestionTemplate(currentQuestion, userGoal);
      const currentQuestionText = currentQ.question || currentQ.content;

      const data = await aiChat({
        task: 'mentor-chat',
        messages: [
            {
              role: "system",
              content: `${MENTOR_SYSTEM_PREAMBLE}

You are an expert AI mentor helping someone achieve their goal: "${userGoal?.title || 'personal development'}".

CURRENT CONTEXT:
- We're on Question ${currentQuestion}/11 of a strategic assessment
- Current question: "${currentQuestionText}"
- Goal: ${userGoal?.description || 'Building success strategy'}

INSTRUCTIONS:
- Have a natural, thoughtful conversation about their response or question
- Reference the conversation history to maintain context
- Help them think deeper about the current question topic
- Be supportive, insightful, and ask follow-up questions when helpful
- DO NOT automatically move to the next question
- Only mention moving forward if they seem ready and ask about it
- Focus on understanding and expanding their thinking on the current topic

CONVERSATION HISTORY:
${conversationHistory.slice(-6).map(msg => `${msg.role}: ${msg.content}`).join('\n')}

Respond naturally to their latest message while staying focused on the current question topic.`
            },
            {
              role: "user",
              content: userInput
            }
        ],
        temperature: 0.7,
        max_tokens: 400
      });
      const aiResponse = data.choices[0].message.content;

      const responseMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: aiResponse,
        timestamp: Date.now()
      };

      setMessages(prev => [...prev, responseMessage]);
    } catch (error) {
      console.error('Error generating conversational response:', error);
      const errorMsg = {
        id: Date.now() + 1,
        type: 'assistant',
        content: `I understand what you're saying. Let me think about that in relation to the current question...

That's an interesting perspective. Can you tell me more about how that connects to what we're discussing? 

When you're ready to move to the next question, just say **"next"** or **"continue"**.`,
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, errorMsg]);
    }
    
    setIsLoading(false);
  };

  return (
    <div className="manifestation-page" role="main">
      <div className="manifestation-header">
        <div className="manifestation-nav-left">
          <button 
            className="manifestation-back-btn" 
            onClick={onBack}
            aria-label="Go back to vision page"
          >
            ← Back to Vision
          </button>
          <button 
            className="start-over-btn" 
            onClick={() => onBack('start-over')}
            aria-label="Start over from beginning"
          >
            🔄 Start Over
          </button>
        </div>
        <h1 className="manifestation-title">AI Development Mentor</h1>
        <button 
          className="complete-setup-btn" 
          onClick={() => {
            console.log('Complete Setup button - businessPlan:', businessPlan);
            console.log('Complete Setup button - businessPlan phases:', businessPlan?.phases);
            onComplete(messages, businessPlan);
          }}
          aria-label="Complete setup and proceed"
        >
          Complete Setup
        </button>
      </div>

      <div className="manifestation-content">
        {/* Vertical Progress Sidebar */}
        <div className="vertical-progress-sidebar">
          <div className="vertical-progress-label">Progress</div>
          <div className="vertical-progress-track">
            <div 
              className="vertical-progress-fill"
              style={{ height: `${(currentQuestion / 11) * 100}%` }}
            ></div>
            <div className="vertical-progress-steps">
              {[...Array(11)].map((_, i) => (
                <div 
                  key={i}
                  className={`vertical-step ${
                    i < currentQuestion ? 'completed' : 
                    i === currentQuestion ? 'active' : 'pending'
                  }`}
                >
                  {i < currentQuestion ? '' : i + 1}
                </div>
              ))}
            </div>
          </div>
          <div className="vertical-progress-percentage">
            {Math.round((currentQuestion / 11) * 100)}%
          </div>
        </div>

        <div className="manifestation-chat">
          
          <div className="manifestation-chat-messages" role="log" aria-live="polite" aria-label="Conversation messages">
            {messages.map(msg => (
              <div key={msg.id} className={`manifestation-message ${msg.type}`} role="article">
                <div className="manifestation-message-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {msg.content}
                  </ReactMarkdown>
                  {msg.hasAutoResearch && msg.type === 'assistant' && currentQuestion === 3 && (
                    <button 
                      onClick={handleAutoResearch}
                      disabled={isLoading}
                      className="auto-research-btn"
                    >
                      {isLoading ? 'Researching...' : 'Auto Research with Delta Technologies'}
                    </button>
                  )}
                </div>
                <div className="manifestation-message-time" aria-label={`Message sent at ${new Date(msg.timestamp).toLocaleTimeString()}`}>
                  {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="manifestation-message assistant" role="status" aria-label="AI is thinking">
                <div className="manifestation-message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <span className="sr-only">AI is processing your response...</span>
                </div>
              </div>
            )}
          </div>

          {planGenerationFailed && !isGeneratingPlan && (
            <button className="plan-retry-btn" onClick={generateComprehensivePlan}>
              Retry plan generation
            </button>
          )}


          <div className="manifestation-chat-input">
            
            {/* Enhanced Text Input */}
            <div className="input-container">
              <label htmlFor="message-input" className="sr-only">
                Type your response or question
              </label>
              <textarea
                id="message-input"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder={getContextualPlaceholder()}
                className="manifestation-input"
                rows={3}
                disabled={isLoading}
                aria-describedby="input-help"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage(e);
                  }
                }}
              />
              <button 
                className="send-btn"
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || isLoading}
                aria-label="Send message"
              >
                {isLoading ? (
                  <>
                    <span className="loading-spinner" aria-hidden="true"></span>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <span>Send</span>
                    <span className="send-icon" aria-hidden="true">→</span>
                  </>
                )}
              </button>
            </div>
            
            {/* Input help text */}
            <div id="input-help" className="input-help">
              {isWaitingForConfirmation ? 
                'Type "yes" to continue or provide additional details' : 
                'Press Enter to send, Shift+Enter for new line'
              }
            </div>
          </div>
        </div>

        {businessPlan && (
          <div className="action-plan-sidebar">
            <h3>{businessPlan.type === 'business_plan' ? 'Your Business Strategy' : 'Your Development Roadmap'}</h3>
            <div className="plan-overview">
              <h4>{businessPlan.goal}</h4>
              <p>{businessPlan.description}</p>
              {businessPlan.type === 'business_plan' && (
                <div className="business-plan-indicator">
                  <span className="plan-type-badge">📋 Complete Business Plan</span>
                  <p><em>Generated from your 11-question assessment</em></p>
                  {businessPlan.conversationHistory && (
                    <div className="conversation-info">
                      <p><strong>Assessment Details:</strong> {businessPlan.conversationHistory.filter(msg => msg.type === 'user').length} questions answered</p>
                      <button 
                        className="view-conversation-btn"
                        onClick={() => setShowBusinessPlan(!showBusinessPlan)}
                      >
                        {showBusinessPlan ? '📖 Hide' : '📖 View'} Question History
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Business Plan Content Section */}
            {businessPlan.type === 'business_plan' && businessPlan.content && (
              <div className="business-plan-content-section">
                <h4>📋 Your Complete Business Plan</h4>
                <div className="business-plan-controls">
                  <button 
                    className="show-full-plan-btn"
                    onClick={() => {
                      setShowFullPlan(true);
                      setVisiblePhases(businessPlan.phases?.length || 0);
                    }}
                  >
                    📄 Show Full Business Plan
                  </button>
                  {showFullPlan && (
                    <button 
                      className="clear-plan-btn"
                      onClick={() => {
                        setShowFullPlan(false);
                        setVisiblePhases(0);
                      }}
                    >
                      🔄 Clear View
                    </button>
                  )}
                </div>
                {showFullPlan && (
                  <div className="business-plan-display">
                    <ReactMarkdown>{businessPlan.content || businessPlan.fullContent}</ReactMarkdown>
                  </div>
                )}
              </div>
            )}
            
            {/* Conversation History Section */}
            {businessPlan.type === 'business_plan' && showBusinessPlan && businessPlan.conversationHistory && (
              <div className="conversation-history-section">
                <h4>📖 Your 11-Question Assessment</h4>
                <div className="conversation-messages">
                  {businessPlan.conversationHistory.map(msg => (
                    <div key={msg.id} className={`history-message ${msg.type}`}>
                      <div className="message-header">
                        <span className="message-type">
                          {msg.type === 'user' ? '👤 You' : '🤖 AI Mentor'}
                        </span>
                        <span className="message-time">
                          {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <div className="message-content-history">
                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Only show phases view when full plan is NOT shown */}
            {!showFullPlan && (
            <div className="plan-phases">
              {/* Phase-by-phase revelation */}
              {businessPlan.phases.slice(0, visiblePhases).map((phase, index) => (
                <div key={index} className="plan-phase visible-phase">
                  <div className="phase-header">
                    <h5>Phase {index + 1}: {phase.title}</h5>
                    <span className="phase-number">{index + 1}/{businessPlan.phases.length}</span>
                  </div>
                  <p className="phase-subtitle">{phase.subtitle}</p>
                  <p className="phase-focus">{phase.focus}</p>
                  <div className="key-metric">
                    <strong>Success Metric:</strong> {phase.keyMetric || phase.successCriteria}
                  </div>
                  
                  {/* Business Strategy Section for business plans */}
                  {phase.businessStrategy && (
                    <div className="phase-section">
                      <h6>🎯 Complete Business Strategy</h6>
                      <div className="business-strategy-content">
                        <ReactMarkdown>{phase.businessStrategy}</ReactMarkdown>
                      </div>
                    </div>
                  )}
                  
                  {/* New precise format sections */}
                  {phase.dailyCommitment && (
                    <div className="phase-section">
                      <h6>⏰ Daily Commitment</h6>
                      <p>{phase.dailyCommitment}</p>
                    </div>
                  )}
                  
                  {phase.weeklyTarget && (
                    <div className="phase-section">
                      <h6>🎯 Weekly Target</h6>
                      <p>{phase.weeklyTarget}</p>
                    </div>
                  )}
                  
                  {phase.specificGoals && (
                    <div className="phase-section">
                      <h6>🎯 Specific Goals</h6>
                      <ul>
                        {phase.specificGoals.map((goal, idx) => (
                          <li key={idx}>{goal}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {phase.dailyRoutine && (
                    <div className="phase-section">
                      <h6>📅 Daily Routine</h6>
                      {Object.entries(phase.dailyRoutine).map(([key, value]) => (
                        <div key={key} className="routine-item">
                          <strong>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</strong> {value}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {phase.measurableSkills && (
                    <div className="phase-section">
                      <h6>📊 Measurable Skills</h6>
                      {phase.measurableSkills.map((skill, idx) => (
                        <div key={idx} className="skill-progression">
                          <strong>{skill.skill}</strong>
                          <ul>
                            <li>Week 1: {skill.week1Target}</li>
                            <li>Week 4: {skill.week4Target}</li>
                            <li>Week 8: {skill.week8Target}</li>
                          </ul>
                          <p><em>Measurement: {skill.measurementMethod}</em></p>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {phase.weeklyMilestones && (
                    <div className="phase-section">
                      <h6>📈 Weekly Milestones</h6>
                      <ul>
                        {phase.weeklyMilestones.map((milestone, idx) => (
                          <li key={idx}>{milestone}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Legacy format sections */}
                  {phase.baselineAssessment && (
                    <div className="phase-section">
                      <h6>📊 Baseline Assessment</h6>
                      <ul>
                        {phase.baselineAssessment.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {phase.smartGoals && !phase.specificGoals && (
                    <div className="phase-section">
                      <h6>🎯 SMART Goals</h6>
                      <ul>
                        {phase.smartGoals.map((goal, idx) => (
                          <li key={idx}>{goal}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {phase.coreSkills && (
                    <div className="phase-section">
                      <h6>🛠️ Core Skills & Drills</h6>
                      <ul>
                        {phase.coreSkills.map((skill, idx) => (
                          <li key={idx}>{skill}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {phase.conditioning && (
                    <div className="phase-section">
                      <h6>💪 Domain-Specific Conditioning</h6>
                      <ul>
                        {phase.conditioning.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {phase.mentalTraining && (
                    <div className="phase-section">
                      <h6>🧠 Mental & Emotional Training</h6>
                      <ul>
                        {phase.mentalTraining.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {(phase.coaching || phase.selfDirectedLearning) && (
                    <div className="phase-section">
                      <h6>🎓 Self-Directed Learning & Community</h6>
                      <ul>
                        {(phase.selfDirectedLearning || phase.coaching).map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {phase.recovery && (
                    <div className="phase-section">
                      <h6>🔄 Recovery & Maintenance</h6>
                      <ul>
                        {phase.recovery.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {phase.periodization && (
                    <div className="phase-section">
                      <h6>📅 Periodization & Scheduling</h6>
                      <ul>
                        {phase.periodization.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {phase.resources && (
                    <div className="phase-section">
                      <h6>💰 Resources & Budget</h6>
                      <ul>
                        {phase.resources.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {phase.evaluation && (
                    <div className="phase-section">
                      <h6>📈 Evaluation & Iteration</h6>
                      <ul>
                        {phase.evaluation.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Fallback for legacy task format */}
                  {phase.tasks && !phase.baselineAssessment && (
                    <div className="phase-section">
                      <h6>📋 Tasks</h6>
                      <ul>
                        {phase.tasks.map((task, taskIndex) => (
                          <li key={taskIndex}>{task}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
              
              {/* Continue button to reveal next phase */}
              {visiblePhases < businessPlan.phases.length && visiblePhases > 0 && (
                <div className="phase-continue-section">
                  <button 
                    className="continue-next-phase-btn"
                    onClick={() => setVisiblePhases(prev => prev + 1)}
                  >
                    ➡️ Continue to Phase {visiblePhases + 1}
                  </button>
                </div>
              )}
              
              {/* Initial start button */}
              {visiblePhases === 0 && businessPlan.phases.length > 0 && (
                <div className="phase-start-section">
                  <button 
                    className="start-phases-btn"
                    onClick={() => setVisiblePhases(1)}
                  >
                    🚀 Start Viewing Business Plan (Phase by Phase)
                  </button>
                </div>
              )}
              
              {/* Show all phases button */}
              {visiblePhases > 0 && visiblePhases < businessPlan.phases.length && (
                <div className="phase-controls-section">
                  <button 
                    className="show-all-phases-btn"
                    onClick={() => setVisiblePhases(businessPlan.phases.length)}
                  >
                    📋 Show All Phases
                  </button>
                </div>
              )}
            </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Extract phases from AI-generated business plan
const extractPhasesFromPlan = (planContent) => {
  if (!planContent) return [];
  
  try {
    // Look for phase sections in the markdown content
    const phaseMatches = planContent.match(/### Phase \d+:[^#]+/g);
    
    if (phaseMatches && phaseMatches.length > 0) {
      return phaseMatches.map((phase, index) => {
        const lines = phase.split('\n');
        const title = lines[0].replace('### ', '').trim();
        const content = lines.slice(1).join('\n').trim();
        
        return {
          title: title,
          subtitle: `Implementation phase ${index + 1}`,
          content: content,
          dayRange: `${index * 30 + 1}-${(index + 1) * 30}`,
          focus: `Phase ${index + 1} execution`,
          keyMetric: `Phase ${index + 1} completion`,
          smartGoals: extractGoalsFromContent(content),
          coreSkills: extractSkillsFromContent(content),
          resources: extractResourcesFromContent(content)
        };
      });
    }
    
    // Fallback: create generic phases based on goal type
    return createDefaultPhases();
  } catch (error) {
    console.error('Error extracting phases from plan:', error);
    return createDefaultPhases();
  }
};

// Helper functions for extracting content
const extractGoalsFromContent = (content) => {
  const goalMatches = content.match(/[-•]\s*([^-•\n]+)/g);
  return goalMatches ? goalMatches.slice(0, 3).map(match => match.replace(/[-•]\s*/, '').trim()) : [];
};

const extractSkillsFromContent = (content) => {
  const skillKeywords = ['develop', 'learn', 'master', 'improve', 'practice', 'build', 'create'];
  const skills = [];
  skillKeywords.forEach(keyword => {
    const regex = new RegExp(`${keyword}\\s+([^.!?\\n]+)`, 'gi');
    const matches = content.match(regex);
    if (matches) {
      skills.push(...matches.slice(0, 2).map(match => match.trim()));
    }
  });
  return skills.slice(0, 3);
};

const extractResourcesFromContent = (content) => {
  const resourceMatches = content.match(/(?:use|utilize|leverage|access|get|find)\s+([^.!?\n]+)/gi);
  return resourceMatches ? resourceMatches.slice(0, 3).map(match => match.trim()) : [];
};

const createDefaultPhases = () => {
  return [
    {
      title: "Phase 1: Foundation Building (Days 1-30)",
      subtitle: "Business strategy and market research",
      dayRange: "1-30",
      focus: "Establishing business foundation",
      keyMetric: "Market research completion and business model validation",
      smartGoals: ["Complete market research", "Validate business model", "Define target audience"],
      coreSkills: ["Market analysis", "Business planning", "Customer research"],
      resources: ["Industry reports", "Competitor analysis tools", "Customer surveys"]
    },
    {
      title: "Phase 2: Development & Growth (Days 31-90)",
      subtitle: "Product development and market entry",
      dayRange: "31-90",
      focus: "Building and launching core offering",
      keyMetric: "Product launch and initial customer acquisition",
      smartGoals: ["Develop MVP", "Launch to market", "Acquire first customers"],
      coreSkills: ["Product development", "Marketing", "Sales"],
      resources: ["Development tools", "Marketing platforms", "Sales channels"]
    },
    {
      title: "Phase 3: Scale & Optimization (Days 91-180)",
      subtitle: "Growth and optimization",
      dayRange: "91-180",
      focus: "Scaling operations and optimizing performance",
      keyMetric: "Revenue growth and operational efficiency",
      smartGoals: ["Scale operations", "Optimize processes", "Expand market reach"],
      coreSkills: ["Operations management", "Process optimization", "Strategic planning"],
      resources: ["Analytics tools", "Automation systems", "Growth platforms"]
    }
  ];
};

export default ManifestationPage;